/* ERS theme — collection and search filters, sorting, grid/list view. Uses Shopify's Section Rendering API. */
(() => {
  'use strict';
  if (window.ERSFacetsLoaded) return;
  window.ERSFacetsLoaded = true;

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const cache = new Map();
  let controller;
  let renderToken = 0;
  let lastRendered = `${window.location.pathname}${window.location.search}`;

  const root = () => document.querySelector('[data-section-id] [data-collection-results]')?.closest('[data-section-id]');

  // Links rendered through the Section Rendering API can carry section_id; never put that in the address bar.
  const cleanUrl = (url) => {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('section_id');
    const query = parsed.searchParams.toString();
    return `${parsed.pathname}${query ? `?${query}` : ''}`;
  };

  const cleanLinks = (scope) => {
    scope.querySelectorAll('a[href*="section_id="]').forEach((link) => {
      link.setAttribute('href', cleanUrl(link.getAttribute('href')));
    });
  };

  function urlFromForm(form) {
    const data = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (value === '') continue;
      params.append(key, value);
    }
    const sort = document.querySelector('[data-sort-select]');
    if (sort && !sort.form) params.set('sort_by', sort.value);
    return `${window.location.pathname}?${params.toString()}`;
  }

  // Keep what someone is typing (price fields) exactly as it is while the markup is replaced.
  function snapshotField() {
    const el = document.activeElement;
    if (!(el instanceof HTMLInputElement) || !el.id) return null;
    let start = null;
    let end = null;
    try {
      start = el.selectionStart;
      end = el.selectionEnd;
    } catch (e) {
      // number inputs don't expose a selection
    }
    return { id: el.id, value: el.value, start, end, typing: el.type === 'text' || el.type === 'number' || el.type === 'search' };
  }

  function restoreField(snapshot) {
    if (!snapshot) return false;
    const el = document.getElementById(snapshot.id);
    if (!el) return false;
    if (snapshot.typing) el.value = snapshot.value;
    el.focus({ preventScroll: true });
    if (snapshot.start !== null) {
      try {
        el.setSelectionRange(snapshot.start, snapshot.end);
      } catch (e) {
        // ignore
      }
    }
    return document.activeElement === el;
  }

  function announce(section) {
    const live = section.querySelector('[data-facets-live]');
    const count = section.querySelector('[data-results-count]');
    if (!live || !count) return;
    live.textContent = '';
    requestAnimationFrame(() => {
      live.textContent = count.textContent.trim();
    });
  }

  async function render(rawUrl, { push = true, focus = null, scroll = false } = {}) {
    const url = cleanUrl(rawUrl);
    const section = root();
    if (!section) {
      window.location.href = url;
      return;
    }
    const sectionId = section.dataset.sectionId;
    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(sectionId)}`;
    // Only the most recent change may update the page.
    const token = ++renderToken;
    controller?.abort();
    section.setAttribute('aria-busy', 'true');
    section.classList.add('is-loading');
    try {
      let html = cache.get(fetchUrl);
      if (!html) {
        controller = new AbortController();
        const response = await fetch(fetchUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(response.status);
        html = await response.text();
        cache.set(fetchUrl, html);
      }
      if (token !== renderToken) return;
      const before = document.activeElement;
      const snapshot = snapshotField();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshResults = doc.querySelector('[data-collection-results]');
      const freshFacets = doc.querySelector('[data-facets-container]');
      const freshLabel = doc.querySelector('[data-results-label]');
      if (freshResults) {
        // Folds in the results (the "Sold out" group) stay open or closed across updates.
        const results = section.querySelector('[data-collection-results]');
        const resultFolds = {};
        results.querySelectorAll('details[id]').forEach((d) => (resultFolds[d.id] = d.open));
        results.innerHTML = freshResults.innerHTML;
        results.querySelectorAll('details[id]').forEach((d) => {
          if (d.id in resultFolds) d.open = resultFolds[d.id];
        });
      }
      const facets = section.querySelector('[data-facets-container]');
      if (freshFacets && facets) {
        const openStates = {};
        const expanded = new Set();
        facets.querySelectorAll('details[id]').forEach((d) => {
          openStates[d.id] = d.open;
          if (d.querySelector('[data-facet-more][aria-expanded="true"]')) expanded.add(d.id);
        });
        facets.innerHTML = freshFacets.innerHTML;
        facets.querySelectorAll('details[id]').forEach((d) => {
          if (d.id in openStates) d.open = openStates[d.id];
          const more = d.querySelector('[data-facet-more]');
          if (more && expanded.has(d.id)) setExpanded(more, true);
        });
      }
      const label = section.querySelector('[data-results-label]');
      if (freshLabel && label) label.innerHTML = freshLabel.innerHTML;
      cleanLinks(section);
      applyView(section);
      if (push) history.pushState({ facets: true }, '', url);

      // Put focus back where it was, or somewhere sensible when the control that was used is gone.
      let focused = before && before !== document.body && before.isConnected && document.activeElement === before;
      if (!focused) focused = restoreField(snapshot);
      if (!focused && focus && focus !== 'results') {
        const target = document.getElementById(focus);
        if (target) {
          target.focus({ preventScroll: true });
          focused = document.activeElement === target;
        }
      }
      if (!focused) {
        const openDrawer = section.querySelector('drawer-panel.is-open');
        const fallback = openDrawer ? openDrawer.querySelector('.drawer__title, [data-drawer-close]') : section.querySelector('[data-results-focus]');
        if (fallback && !fallback.hasAttribute('tabindex') && !fallback.matches('a, button, input, select, textarea')) fallback.setAttribute('tabindex', '-1');
        fallback?.focus({ preventScroll: !scroll });
      }
      announce(section);
      lastRendered = url;

      if (scroll) {
        const top = section.querySelector('[data-collection-results]').getBoundingClientRect().top + window.scrollY - 140;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = url;
    } finally {
      if (token === renderToken) {
        section.removeAttribute('aria-busy');
        section.classList.remove('is-loading');
      }
    }
  }

  const onFormChange = debounce((event) => {
    const form = event.target.closest('[data-facets-form]') || event.target.form;
    if (!form) return;
    render(urlFromForm(form), { focus: event.target.id || 'results' });
  }, 500);

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-sort-select]')) {
      const form = document.querySelector('[data-facets-form]');
      if (form) {
        render(urlFromForm(form), { focus: event.target.id });
      } else {
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', event.target.value);
        params.delete('page');
        render(`${window.location.pathname}?${params.toString()}`, { focus: event.target.id });
      }
      return;
    }
    if (event.target.closest('[data-facets-form]')) onFormChange(event);
  });

  // A link with role="switch" should also respond to the space bar, like a real switch.
  document.addEventListener('keydown', (event) => {
    if (event.key !== ' ' || event.repeat) return;
    const toggle = event.target.closest?.('a[role="switch"][data-facet-link]');
    if (!toggle) return;
    event.preventDefault();
    toggle.click();
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-facets-form] input[inputmode="decimal"]')) onFormChange(event);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (!form) return;
    event.preventDefault();
    render(urlFromForm(form), { focus: 'results' });
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-facet-link]');
    if (link && root()) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      const isPagination = !!link.closest('.pagination');
      // Switches (In stock only) flip straight away and keep focus once the results are back. A second
      // press while the results are loading would only repeat the same request, so it's ignored.
      if (link.getAttribute('role') === 'switch') {
        if (root().getAttribute('aria-busy') === 'true') return;
        link.setAttribute('aria-checked', String(link.getAttribute('aria-checked') !== 'true'));
      }
      render(link.getAttribute('href'), { scroll: isPagination, focus: link.id || 'results' });
      return;
    }
    const more = event.target.closest('[data-facet-more]');
    if (more) {
      setExpanded(more, more.getAttribute('aria-expanded') !== 'true');
      return;
    }
    const viewButton = event.target.closest('[data-view]');
    if (viewButton) {
      const view = viewButton.dataset.view;
      try {
        localStorage.setItem('ers-view', view);
      } catch (e) {}
      applyView(document, view);
      return;
    }
    const descriptionToggle = event.target.closest('.collection-header__more');
    if (descriptionToggle) {
      const about = descriptionToggle.closest('[data-clamp]');
      const expanded = descriptionToggle.getAttribute('aria-expanded') === 'true';
      about.classList.toggle('is-clamped', expanded);
      descriptionToggle.setAttribute('aria-expanded', String(!expanded));
      descriptionToggle.textContent = expanded ? descriptionToggle.dataset.labelMore : descriptionToggle.dataset.labelLess;
    }
  });

  function setExpanded(more, expanded) {
    const list = more.previousElementSibling;
    list?.classList.toggle('is-expanded', expanded);
    more.setAttribute('aria-expanded', String(expanded));
    more.querySelector('[data-more]').hidden = expanded;
    more.querySelector('[data-less]').hidden = !expanded;
  }

  function applyView(scope = document, forced) {
    let view = forced;
    if (!view) {
      try {
        view = localStorage.getItem('ers-view');
      } catch (e) {}
    }
    scope.querySelectorAll('[data-view-grid]').forEach((grid) => {
      if (view) grid.classList.toggle('is-list', view === 'list');
      const isList = grid.classList.contains('is-list');
      scope.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String((b.dataset.view === 'list') === isList)));
    });
  }

  // Long collection descriptions start clamped to three lines (server-side). Once the browser has laid
  // the text out, show the Read more button only if there is more to read.
  function initDescriptions() {
    const abouts = document.querySelectorAll('[data-clamp]');
    if (!abouts.length) return;
    const measure = (about) => {
      const text = about.querySelector('.collection-header__description');
      const toggle = about.querySelector('.collection-header__more');
      if (!text || !toggle || toggle.getAttribute('aria-expanded') === 'true') return;
      toggle.hidden = text.scrollHeight <= text.clientHeight + 4;
    };
    if (!('ResizeObserver' in window)) {
      abouts.forEach(measure);
      return;
    }
    const observer = new ResizeObserver((entries) => entries.forEach((entry) => measure(entry.target.closest('[data-clamp]'))));
    abouts.forEach((about) => {
      const text = about.querySelector('.collection-header__description');
      if (text) observer.observe(text);
    });
  }

  window.addEventListener('popstate', () => {
    const current = `${window.location.pathname}${window.location.search}`;
    // A change to the #hash only (skip link, anchors) isn't a new filter state.
    if (current === lastRendered) return;
    render(current, { push: false });
  });

  applyView();
  initDescriptions();
})();
